import { useEffect, useState, useRef } from "react";
import throttle from "lodash.throttle";

interface MousePosition {
  x: number;
  y: number;
}

interface UseMousePositionProps {
  track?: boolean;
  throttleMs?: number;
}

const isWindowDefined = typeof window !== "undefined";

export const useMousePosition = ({
  track = true,
  throttleMs = 250
}: UseMousePositionProps = {}) => {
  const ref = useRef<HTMLElement | Window>(isWindowDefined ? window : null);
  const [position, setPosition] = useState<MousePosition>({ x: 0, y: 0 });

  useEffect(() => {
    const element = ref.current;

    if (!track || !element) {
      return;
    }

    const handleMouseMove = throttle(
      (e: MouseEvent) => setPosition({ x: e.clientX, y: e.clientY }),
      throttleMs,
      { trailing: true }
    );

    element.addEventListener("mousemove", handleMouseMove);

    return () => {
      element.removeEventListener("mousemove", handleMouseMove);
      handleMouseMove.cancel();
    };
  }, [track, throttleMs]);

  return [ref, position] as const;
};
